import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import SEO from './SEO';
import './ResetPassword.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.defaults.withCredentials = true;

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', newPassword: '', confirmPassword: '' });
  const [touched, setTouched] = useState({ email: false, newPassword: false, confirmPassword: false });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [tokenLoading, setTokenLoading] = useState(true);

  // Validation functions
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      return 'Email address is required';
    }
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      return 'Password is required';
    }
    // Password must have:
    // - At least 8 characters
    // - At least one uppercase letter
    // - At least one lowercase letter
    // - At least one number
    // - At least one special character (symbol)
    const minLength = passwordValue.length >= 8;
    const hasUpperCase = /[A-Z]/.test(passwordValue);
    const hasLowerCase = /[a-z]/.test(passwordValue);
    const hasNumber = /[0-9]/.test(passwordValue);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passwordValue);

    if (!minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (!@#$%^&* etc.)';
    }
    return '';
  };

  const validateConfirmPassword = (confirmValue, passwordValue) => {
    if (!confirmValue) {
      return 'Please confirm your password';
    }
    if (confirmValue !== passwordValue) {
      return 'Passwords do not match';
    }
    return '';
  };

  // Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(email) }));
    } else if (field === 'newPassword') {
      setFieldErrors(prev => ({ ...prev, newPassword: validatePassword(newPassword) }));
      // Also validate confirm password if it's been touched
      if (touched.confirmPassword) {
        setFieldErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(confirmPassword, newPassword) }));
      }
    } else if (field === 'confirmPassword') {
      setFieldErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(confirmPassword, newPassword) }));
    }
  };

  // Handle field changes
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    if (touched.newPassword) {
      setFieldErrors(prev => ({ ...prev, newPassword: validatePassword(value) }));
    }
    // If confirm password is touched, revalidate it
    if (touched.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(confirmPassword, value) }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (touched.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(value, newPassword) }));
    }
  };

  // Extract token from URL (searchParams.get automatically decodes URL-encoded values)
  // Trim to handle any whitespace issues
  const rawToken = searchParams.get('token');
  const token = rawToken ? rawToken.trim() : null;
  const emailFromUrl = searchParams.get('email'); // Optional email from URL

  // Verify token when component loads
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenLoading(false);
        setError('Invalid or missing reset token');
        return;
      }

      // If email is in URL, verify token with email
      if (emailFromUrl) {
        try {
          const response = await axios.get(`${API_URL}/auth/verify-reset-token`, {
            params: { token, email: emailFromUrl }
          });

          if (response.data.valid) {
            setTokenValid(true);
            setEmail(response.data.email || emailFromUrl);
          } else {
            setTokenValid(false);
            setError(response.data.message || 'Invalid or expired reset token');
          }
        } catch (err) {
          setTokenValid(false);
          setError('Failed to verify reset token. Please request a new password reset link.');
        } finally {
          setTokenLoading(false);
        }
      } else {
        // No email in URL, just mark as checking (user will enter email)
        setTokenValid(true); // Assume valid, will verify on submit
        setTokenLoading(false);
      }
    };

    verifyToken();
  }, [token, emailFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Mark all fields as touched
    setTouched({ email: true, newPassword: true, confirmPassword: true });

    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(newPassword);
    const confirmPasswordError = validateConfirmPassword(confirmPassword, newPassword);

    setFieldErrors({
      email: emailError,
      newPassword: passwordError,
      confirmPassword: confirmPasswordError
    });

    // If there are validation errors, don't submit
    if (emailError || passwordError || confirmPasswordError) {
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setLoading(true);

    try {
      console.log('Sending reset request - Token length:', token?.length, 'Token (first 10 chars):', token?.substring(0, 10) + '...', 'Email:', email);
      // Reset password endpoint - credentials may be needed for CORS
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        email,
        password: newPassword,
      });

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);

      // Handle CORS/401 errors specifically
      if (err.response?.status === 401) {
        setError('Authentication error. Please check your connection and try again.');
      } else {
        const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to reset password. Please try again.';
        setError(errorMessage);

        // Check if this is a Google OAuth user error
        if (errorMessage.includes('Google Sign-In') || errorMessage.includes('Google Sign')) {
          // Show the error prominently for Google OAuth users
          setFieldErrors(prev => ({ ...prev, email: errorMessage }));
        } else if (errorMessage.toLowerCase().includes('email')) {
          setFieldErrors(prev => ({ ...prev, email: errorMessage }));
        } else if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('token')) {
          // Token errors are general, but password errors can be shown
          if (errorMessage.toLowerCase().includes('password')) {
            setFieldErrors(prev => ({ ...prev, newPassword: errorMessage }));
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2 className="success-title">Password Reset Successful</h2>
              <p className="success-text">
                Your password has been reset successfully. Redirecting to login...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while verifying token
  if (tokenLoading) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div style={{ textAlign: 'center', padding: '50px' }}>Verifying reset token...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="error-message" style={{ textAlign: 'center', padding: '30px' }}>
              <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>Invalid Reset Link</h2>
              <p>{error || 'This password reset link is invalid or has expired.'}</p>
              <p style={{ marginTop: '20px' }}>
                <Link to="/forgot-password" style={{ color: '#007bff', textDecoration: 'underline' }}>
                  Request a new password reset link
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <SEO title="Set New Password" path="/reset-password" description="Set a new password for your LawFusion account." noIndex={true} />
      <div className="reset-password-container">
        <div className="reset-password-card">
          {/* Title */}
          <h1 className="reset-password-title">Reset Your Password</h1>
          <p className="reset-password-tagline">Enter your email and new password to reset your account.</p>

          {/* Reset Password Form */}
          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => handleBlur('email')}
                required
                placeholder="you@firm.com"
                readOnly={!!emailFromUrl} // Read-only if email came from URL
                className={touched.email && fieldErrors.email ? 'input-error' : emailFromUrl ? 'input-readonly' : ''}
              />
              {touched.email && fieldErrors.email && (
                <div className="field-error">
                  {fieldErrors.email}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  onBlur={() => handleBlur('newPassword')}
                  required
                  placeholder="Enter new password"
                  minLength="8"
                  className={touched.newPassword && fieldErrors.newPassword ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.newPassword && fieldErrors.newPassword && (
                <div className="field-error">
                  {fieldErrors.newPassword}
                </div>
              )}
              {!touched.newPassword || !fieldErrors.newPassword ? (
                <p style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  marginTop: '0.5rem',
                  marginBottom: 0,
                  lineHeight: '1.4'
                }}>
                  Password must contain: 8+ characters, uppercase, lowercase, number, and special character
                </p>
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={() => handleBlur('confirmPassword')}
                  required
                  placeholder="Confirm new password"
                  minLength="8"
                  className={touched.confirmPassword && fieldErrors.confirmPassword ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.confirmPassword && fieldErrors.confirmPassword && (
                <div className="field-error">
                  {fieldErrors.confirmPassword}
                </div>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Reset Button */}
            <button type="submit" className="reset-button" disabled={loading}>
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          {/* Back to Login Link */}
          <p className="back-to-login">
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

